import { NetworkStatus } from '@apollo/client'
import SingleLineEllipsis from '@deltek/specification-client/core/Feedbacks/SingleLineEllipsis'
import { UserWithProjectRole } from '@deltek/specification-client/core/Models/firm'
import { GetFilterInput } from '@deltek/specification-client/core/Models/projectItem'
import GridContainer from '@deltek/specification-client/core/Surfaces/GridContainer'
import GridItem from '@deltek/specification-client/core/Surfaces/GridItem'
import { isEqual } from '@deltek/specification-client/core/Utilities/objectUtils'
import { pxToRem } from '@deltek/specification-client/core/Utilities/unitUtils'
import {
    SpecColumn,
    SpecFilters,
    SpecIdType,
    SpecSortingRule,
    SpecTable,
    SpecTableState,
    TableFetchDataProps,
    TableFetchNextDataProps,
} from '@deltek/specification-client/core/Widgets/SpecTable'
import { SpecTableTitleSettingsProps } from '@deltek/specification-client/core/Widgets/SpecTable/SpecTableTitleSettings'
import useSpecTableOptions from '@deltek/specification-client/core/Widgets/SpecTable/useSpecTableOptions'
import { Theme, makeStyles } from '@material-ui/core/styles'
import { Assignment } from 'Models/project'
import clsx from 'clsx'
import { ExecutionResult } from 'graphql'
import Popout from 'pages/ProjectsPage/ProjectsHomePage/components/Popout'
import ProjectTeamMemberPopoutContent from 'pages/ProjectsPage/ProjectsHomePage/components/ProjectTeamMemberPopoutContent'
import React, { useMemo, useRef, useState } from 'react'
import { CellProps, Column, IdType } from 'react-table'
import { ProjectTeamFilterContext } from './ProjectTeamFilterContext'
import ProjectTeamGridNameCell from './ProjectTeamGridNameCell'

const useStyles = makeStyles((theme: Theme) => ({
    tableViewContainer: {
        paddingTop: 0,
    },
    actionIcon: {
        color: theme.palette.secondary.main,
    },
    popoutParent: {
        zIndex: theme.zIndex.modal + 3,
    },
    specTableStyle: {
        '& .table-title-container': {
            fontSize: pxToRem(16),
            textTransform: 'uppercase',
            marginLeft: theme.spacing(-1.5),
        },
        '& .MuiSvgIcon-root.arrow-down, .MuiSvgIcon-root.arrow-right': {
            marginLeft: theme.spacing(-1),
            color: 'rgba(0, 0, 0, 0.54)',
        },
        '& .MuiGrid-justify-content-xs-space-between': {
            backgroundColor: '#343334',
        },
        '& .MuiList-padding': {
            maxHeight: 300,
        },
        '& .table-title-settings-container': {
            '& .MuiDivider-vertical': {
                display: 'none',
            },
            display: (canToggleGroup: boolean) =>
                canToggleGroup ? 'flex' : 'none',
        },
        '& .light button.expand-all-button, .light button.collapse-all-button': {
            color: `${theme.palette.primary.main}`,
            border: `1px solid ${theme.palette.primary.main}`,
        },
        '& .MuiButton-containedPrimary': {
            backgroundColor: '#343334',
            color: theme.palette.common.white,
        },
        '& .view-as-table.active, .view-filter.active': {
            backgroundColor: theme.palette.primary.main,
        },
        '& .load-next-button': {
            backgroundColor: theme.palette.primary.main,
            color: theme.palette.common.white,
        },
        '& .tr.grouped-row.expanded .td.grouped-cell': {
            backgroundColor: theme.palette.common.white,
        },
        '& .td.grouped-cell': {
            color: '#343334',
            fontWeight: 'bold',
        },
        '& .td': {
            fontSize: pxToRem(12),
        },
        '& .sticky .header': {
            zIndex: 5,
        },
    },
}))

interface ProjectTeamGridProps {
    usersData: UserWithProjectRole[] | undefined
    fetchNextData: (
        props: Partial<TableFetchNextDataProps<UserWithProjectRole>>
    ) => void
    userDataStatus?: number
    defaultPageSize?: number
    setSortBy?: (val: SpecSortingRule<UserWithProjectRole>[]) => void
    sortBy?: SpecSortingRule<UserWithProjectRole>[]
    userCount: number
    setSelectedRowIds: (
        val: Record<IdType<UserWithProjectRole>, boolean>
    ) => void
    selectedRowIds: Record<IdType<UserWithProjectRole>, boolean>
    getUserAssignment: (
        userId: string
    ) => null | Partial<Assignment> | undefined
    updateAssignment?: (
        projectRole: string,
        description: string,
        userid: string
    ) => Promise<
        ExecutionResult<{
            updateProjectTeamAssignment: Partial<Assignment> | null
        }>
    >
    updateAssignmentLoading: boolean
    getRoles: (disciplineName: string) => Record<'name' | 'value', string>[]
    canToggleGroup: boolean
}

const groupBy = ['discipline']

export const ProjectTeamGrid: React.FC<ProjectTeamGridProps> = ({
    usersData,
    fetchNextData,
    userDataStatus,
    defaultPageSize,
    setSortBy,
    sortBy,
    userCount,
    setSelectedRowIds,
    selectedRowIds,
    getUserAssignment,
    updateAssignment,
    updateAssignmentLoading,
    getRoles,
    canToggleGroup,
}) => {
    const classes = useStyles(canToggleGroup)

    const tableRef = useRef<HTMLDivElement>(null)
    const divContainer = useRef<HTMLDivElement>()
    const { userFilterValues, setUserFilterValues } = React.useContext(
        ProjectTeamFilterContext
    )
    const [filters, setFilters] = useState<SpecFilters<UserWithProjectRole>>([])
    const [expanded, setExpanded] = useState<
        Record<SpecIdType<UserWithProjectRole>, boolean>
    >(() => {
        const currentState: Record<
            SpecIdType<UserWithProjectRole>,
            boolean
        > = {}
        if (!!usersData && usersData.length > 0) {
            currentState[
                `discipline:${usersData[0].user.profileInfo?.professionalDiscipline}`
            ] = true
        }
        return currentState
    })

    const [initialExpandState, setInitialExpandState] = useState<boolean>(true)

    const [nameAnchor, setNameAnchor] = React.useState<null | Element>(null)
    const [assigment, setAssignment] = React.useState<
        null | Partial<Assignment> | undefined
    >()
    const [userWithProjectRole, setuserWithProjectRole] = React.useState<
        null | UserWithProjectRole | undefined
    >()

    const handlePopoutClose = () => {
        setNameAnchor(null)
        setuserWithProjectRole(null)
        setAssignment(null)
    }

    const onStateChange = React.useCallback(
        (state: SpecTableState<UserWithProjectRole>) => {
            if (!isEqual(state.selectedRowIds, selectedRowIds)) {
                setSelectedRowIds?.(state.selectedRowIds)
            }
            if (!isEqual(state.expanded, expanded)) {
                setExpanded(state.expanded)
            }
            if (!isEqual(state.sortBy, sortBy)) {
                setSortBy?.(state.sortBy)
            }
        },
        [
            setSelectedRowIds,
            setExpanded,
            setSortBy,
            selectedRowIds,
            expanded,
            sortBy,
        ]
    )

    const columns = useMemo<SpecColumn<UserWithProjectRole>[]>(
        () => [
            {
                Header: 'DISCIPLINE',
                id: 'discipline',
                accessor: (row) =>
                    row?.user?.profileInfo?.professionalDiscipline,
                minWidth: 80,
                maxWidth: 100,
            },
            {
                Header: 'NAME',
                id: 'name',
                accessor: (row) =>
                    `${row?.user?.profileInfo?.firstName!} ${row?.user
                        ?.profileInfo?.lastName!}`,
                minWidth: 250,
                // The cell can use the individual row's getToggleRowSelectedProps method
                // to the render a favorite icon
                Cell: (nameCell: CellProps<UserWithProjectRole, string>) => {
                    if (nameCell.cell.isAggregated) return null
                    return (
                        <ProjectTeamGridNameCell
                            nameCell={nameCell}
                            selectedUserId={userWithProjectRole?.user.id!}
                            onNameClick={(event, userWithProjectRole) => {
                                setuserWithProjectRole(userWithProjectRole)
                                setAssignment(
                                    getUserAssignment(
                                        userWithProjectRole.user.id!
                                    )
                                )
                                setNameAnchor(event.currentTarget)
                            }}
                        />
                    )
                },
            },
            {
                Header: 'ROLE',
                id: 'role',
                accessor: (row) => row?.projectRole,
                minWidth: 180,
                Cell: ({ cell }: CellProps<UserWithProjectRole, string>) => {
                    if (cell.isAggregated) return null
                    return <SingleLineEllipsis text={cell.value} />
                },
            },
            {
                Header: 'EMAIL',
                id: 'email',
                accessor: (row) => row?.user?.email,
                minWidth: 250,
                Cell: ({ cell }: CellProps<UserWithProjectRole, string>) => {
                    if (cell.isAggregated) return null
                    return <SingleLineEllipsis text={cell.value} />
                },
            },
        ],
        [getUserAssignment, userWithProjectRole]
    )

    const fetchData = React.useCallback(
        ({
            filters: updatedFilters,
            sortBy: updatedSortBy,
        }: TableFetchDataProps<UserWithProjectRole>) => {
            setSortBy?.(updatedSortBy)
            if (!!updatedFilters) {
                setFilters(updatedFilters)
            }
        },
        [setFilters, setSortBy]
    )

    const tableProp = useMemo(() => {
        return {
            allowGrouping: true,
            defaultColumn: { width: 100 } as Column<UserWithProjectRole>,
            columns,
            data: usersData || [],
            divContainerRef: divContainer,
            expanded,
            fetchData,
            fetchDataLoading:
                userDataStatus !== NetworkStatus.ready &&
                userDataStatus !== NetworkStatus.fetchMore,
            fetchNextData,
            fetchNextDataLoading: userDataStatus !== NetworkStatus.ready,
            filters: tableFilterInput(userFilterValues),
            groupBy,
            height: canToggleGroup ? 300 : 350,
            onStateChange,
            pageSize: defaultPageSize,
            showCheckboxColumn: true,
            specTableTitleSettings: {
                hasGrouping: canToggleGroup,
                showFiltersRow: true,
                showTableSettings: false,
                tableViewActive: false,
                theme: 'light',
                tableSettings: {
                    canToggleGroup,
                    initialPinningProps: {
                        firstColumnPinned: false,
                        lastColumnPinned: false,
                    },
                },
            } as SpecTableTitleSettingsProps<UserWithProjectRole>,
            totalItems: userCount,
            selectedRowIds,
        }
    }, [
        columns,
        expanded,
        fetchNextData,
        userDataStatus,
        userCount,
        defaultPageSize,
        fetchData,
        userFilterValues,
        usersData,
        selectedRowIds,
        onStateChange,
        canToggleGroup,
    ])
    const specTableProps = useSpecTableOptions<UserWithProjectRole>(tableProp)

    React.useEffect(() => {
        if (
            !canToggleGroup &&
            initialExpandState &&
            (usersData || []).length > 0
        ) {
            const initialExpandedVal: Record<
                SpecIdType<UserWithProjectRole>,
                boolean
            > = {}
            if (tableRef.current) tableRef.current.scrollTop = 0
            const uniqueGroups = usersData
                ?.map((item) => item.user.profileInfo?.professionalDiscipline)
                .filter((value, index, self) => self.indexOf(value) === index)

            uniqueGroups?.forEach((group, index) => {
                initialExpandedVal[`discipline:${group}`] = true
            })
            setExpanded(initialExpandedVal)
            setInitialExpandState(false)
        }
    }, [usersData, canToggleGroup, initialExpandState])

    React.useEffect(() => {
        const projectTeamFilterValuesList: Partial<GetFilterInput>[] = []
        if (filters) {
            filters.forEach((item: any) => {
                if (item.value?.length) {
                    const obj: Partial<GetFilterInput> = {
                        itemField: item.id,
                        filterValues: Array.isArray(item.value)
                            ? item.value
                            : [item.value],
                    }
                    projectTeamFilterValuesList.push(obj)
                }
            })
            setUserFilterValues(projectTeamFilterValuesList)
        }
    }, [filters, setUserFilterValues])

    return (
        <>
            <GridContainer
                className={classes.tableViewContainer}
                data-testid="projectTeamGrid"
            >
                <GridItem
                    xs={12}
                    ref={divContainer}
                    className={classes.specTableStyle}
                >
                    <SpecTable {...specTableProps} />
                </GridItem>
            </GridContainer>
            <Popout
                theme="dark"
                offset="100"
                placement="right-end"
                className={clsx('projectTeamPopoutInfo', classes.popoutParent)}
                anchorEl={nameAnchor}
                onCloseRequest={handlePopoutClose}
            >
                <ProjectTeamMemberPopoutContent
                    userWithProjectRole={userWithProjectRole!}
                    assignment={assigment}
                    updateAssignment={updateAssignment!}
                    handlePopoutClose={handlePopoutClose}
                    updateAssignmentLoading={updateAssignmentLoading}
                    getRoles={getRoles}
                />
            </Popout>
        </>
    )
}

const tableFilterInput = (filter: Partial<GetFilterInput>[]) => {
    const filterValue: SpecFilters<UserWithProjectRole> = []
    if (filter && filter.length) {
        filter.forEach((item: Partial<GetFilterInput>) => {
            if (item && item.filterValues?.length) {
                filterValue.push({
                    id: item?.itemField ?? '',
                    value: item?.filterValues?.[0] ?? [],
                })
            }
        })
    }
    return filterValue
}
export default ProjectTeamGrid
